import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session flag (cleared on tab close) — used to enforce auto-logout on tab close.
const SESSION_KEY = 'lbc_session_active';
// Bearer token fallback (needed on Chrome Android / Safari ITP where 3rd-party
// cookies are blocked between gestion-lbc.com <-> *.emergentagent.com).
const TOKEN_KEY = 'lbc_access_token';

const readToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
};
const writeToken = (t) => {
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) { /* ignore Safari private mode */ }
};

// Global axios request interceptor: attach Bearer token whenever we have one.
// This runs once at module load, so every axios call in the whole app benefits.
axios.interceptors.request.use((config) => {
  try {
    const token = readToken();
    if (token && config && config.headers) {
      // Don't overwrite an explicit Authorization header if the caller already set one.
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) { /* ignore */ }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      // Cookie or Bearer token says we're authenticated. But if the sessionStorage
      // flag is missing, the tab was previously closed → force logout.
      let sessionActive = true;
      try {
        sessionActive = sessionStorage.getItem(SESSION_KEY) === '1';
      } catch (e) {
        // If sessionStorage is inaccessible (Safari private mode), don't force logout —
        // rely purely on the cookie/token so users can still use the app.
        sessionActive = true;
      }
      if (!sessionActive) {
        try {
          await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
        } catch (e) { /* ignore */ }
        writeToken(null);
        setUser(false);
      } else {
        setUser(response.data);
      }
    } catch (error) {
      try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
      writeToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { username, password },
        { withCredentials: true }
      );
      // Persist the Bearer token so the axios interceptor can attach it on
      // subsequent requests when 3rd-party cookies are blocked by the browser.
      if (response.data?.access_token) {
        writeToken(response.data.access_token);
      }
      // sessionStorage may throw in Safari private mode / when quota exceeded;
      // never let this break the login flow.
      try {
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch (e) { /* ignore — session flag optional */ }
      setUser(response.data);
      return { success: true };
    } catch (error) {
      let errorMsg;
      if (error.response?.data?.detail != null) {
        errorMsg = formatApiErrorDetail(error.response.data.detail);
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMsg = "Erreur réseau — vérifiez votre connexion internet et réessayez.";
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = "La requête a expiré. Vérifiez votre connexion et réessayez.";
      } else {
        errorMsg = error.message || "Une erreur s'est produite. Veuillez réessayer.";
      }
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
      writeToken(null);
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
